import React, { useRef, useState } from 'react'
import cx from 'classnames'
import { Form, Select as AntdSelect } from 'antd'
import { SelectProps } from 'antd/lib/select'

import styles from './Dropdown.module.scss'
import Input from '../Input'

const { Option } = AntdSelect

export type DropdownOption<T> = {
  value: T
  label: string
  type?: string
}

type Props<T> = {
  alphabetical?: boolean
  className?: string
  label?: React.ReactNode
  id: string
  name: string
  options: DropdownOption<T>[]
  required?: boolean
  strong?: boolean
  searchable?: boolean
}

const Dropdown = <T extends string | number>(
  props: Props<T> & SelectProps<T>
) => {
  const {
    alphabetical,
    className,
    label,
    name,
    id,
    options,
    required,
    searchable = false,
    ...selectProps
  } = props
  const selectOptions = alphabetical ? sortByLabel(options) : options

  return (
    <Form.Item
      className={cx(
        styles.dropdownItem,
        selectProps.disabled && styles.disabled,
        className
      )}
      name={name}
      label={label}
      rules={
        required
          ? [{ required: true, message: 'This field is required.' }]
          : undefined
      }
    >
      <Select
        {...selectProps}
        id={id}
        options={selectOptions}
        searchable={searchable}
      />
    </Form.Item>
  )
}

export const Select = <T extends string | number>(
  props: {
    options: DropdownOption<string | number>[]
    id: string
    strong?: boolean
    fontSize?: 'sm'
    searchable?: boolean
  } & Omit<SelectProps<T>, 'options'>
) => {
  const {
    id,
    className,
    fontSize,
    options,
    strong,
    searchable = false,
    ...selectProps
  } = props

  const ref = useRef(null)
  const [filter, setFilter] = useState('')

  // Filter options based on input only if searchable
  let filteredOptions = options
  if (searchable) {
    filteredOptions = options.filter(
      (option) =>
        option.label.toLowerCase().includes(filter.toLowerCase()) ||
        option.value.toString().toLowerCase().includes(filter.toLowerCase())
    )
  }

  // Wrap onChange to reset filter after selection
  const handleChange = (value: any, option: any) => {
    if (selectProps.onChange) {
      selectProps.onChange(value, option)
    }
    setFilter('')
  }

  // Also reset filter on select (for immediate feedback)
  const handleSelect = (value: any, option: any) => {
    if (selectProps.onSelect) {
      selectProps.onSelect(value, option)
    }
    setFilter('')
  }

  return (
    <div ref={ref} id={id}>
      <AntdSelect
        {...selectProps}
        onChange={handleChange}
        onSelect={handleSelect}
        className={cx(
          styles.select,
          {
            [styles.sm]: fontSize === 'sm',
            [styles.strong]: strong,
          },
          className
        )}
        dropdownClassName={cx(styles.dropdown, {
          [styles.dropdownSm]: fontSize === 'sm',
        })}
        dropdownRender={(menu) => (
          <div>
            {searchable ? (
              <div style={{ padding: 8 }}>
                <Input
                  noFormItem
                  placeholder="Type to filter..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className={styles.dropdownFilter}
                  bordered={false}
                  autoComplete="off"
                />
              </div>
            ) : null}
            {menu}
          </div>
        )}
      >
        {filteredOptions.map((option, index) => (
          <Option
            id={index === 0 ? option.type : `${option.type}_${index}`}
            className={styles.option}
            key={option.value}
            title={option.label || option.value.toString()}
            value={option.value}
          >
            {option.label || option.value}
          </Option>
        ))}
      </AntdSelect>
    </div>
  )
}

const sortByLabel = (arr: DropdownOption<string | number>[]) =>
  arr.sort((a, b) => {
    const labelA = a.label || a.value
    const labelB = b.label || b.value
    if (labelA < labelB) return -1
    else if (labelA > labelB) return 1
    else return 0
  })

export default Dropdown
